import React, {
  createElement,
  forwardRef,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
  type LegacyRef,
} from 'react';
import { WidthProvider } from 'react-grid-layout';
import * as R from '../internal/utils/record';
import type { EnhancedResponsiveGridLayoutProps } from './EnhancedResponsiveGridLayout';
import { EnhancedResponsiveGridLayout } from './EnhancedResponsiveGridLayout';

import type {
  ApiDashboardLayoutParametersType,
  ApiItemType,
  ApiReturnCampaign,
  ApiReturnDashboard,
  ApiReturnProject,
  ApiReturnSite,
  ReportDashboard,
  ReportStructure,
} from '@highimpact-frontend/api-types';
import {
  DropDownMenu,
  GenericErrorBoundary,
  StateComponent,
  type DropDownMenuItem,
} from '@highimpact/dashboard-ui';
import { PermissionDecorator } from '@highimpact/react-decorators';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { DashboardDataApi } from '../api/DashboardHostApi';
import { useDashboardHost } from '../context/DashboardHostContext';
import ConfigWidgetFormHandler from '../forms/ConfigWidgetFormHandler';
import InitiWidgetFormHandler from '../forms/InitiWidgetFormHandler';
import {
  emptyGridHydrationSnapshot,
  useGridHydrationExternals,
  type GridHydrationExternalsStatus,
  type GridHydrationSnapshot,
} from '../hooks/useGridHydrationExternals';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
  type DashboardLibraryDispatch,
} from '../runtime/DashboardLibraryProvider';
import { hydrateDashboardLibraryState } from '../state/actions';
import { hasAvailableWidgetSlots } from '../state/dashboard-layout-operations';
import {
  setDashboardForm,
  setDashboardLayout,
  setWidgetConfigPanelState,
  setWidgetFormIsOpen,
} from '../state/dashboard/slice';
import {
  widget_configurations,
  WidgetNameType,
  WidgetParamsFromName,
} from '../state/dashboard/widget';
import { buildMapping } from '../state/report-structure-operations';
import { fetchSourceLabelMapForElements } from '../state/resolveSourceLabels';
import {
  addDashboardSafely,
  getDashboardElementIdMappingFromDashboard,
  getWidgetFromIdMapping,
} from '../state/store-operations';
import RemoteConfigSidePanel from './config/RemoteConfigSidePanel';
import DashboardTabBarComponent from './DashboardTabBarComponent';
import PublicDashboardButtonComponent from './PublicDashboardButtonComponent';
import { SvgConstrainedGridLayout } from './SvgConstrainedGridLayout';
import DeleteWidgetConfirmation from './widget/commons/DeleteWidgetConfirmation';
import {
  useWidgetCreationInfo,
  WidgetCreationData,
} from './widget/useWidgetCreationInfo';
import {
  buildWidgetDropdownItems,
  getBuiltinGroupConfig,
} from './widget/widgetGroups';
import ImageWidgetConfig from './widget/widgets/image/image/imageWidgetConfig';
import ImageGalleryConfig from './widget/widgets/imageGallery/imageGallery/imageGalleryConfig';

// -- type defintions --

interface WidgetIdToDashboardElementId {
  [widgetId: string]: number;
}

interface DashboardItemPermissions {
  itemNameCreateBtn?: ApiItemType;
  itemNameDeleteBtn?: ApiItemType;
  itemNameEditEl?: ApiItemType;
  itemNameAddWidget?: ApiItemType;
}

export interface GridComponentProps extends DashboardItemPermissions {
  project?: ApiReturnProject;
  site?: ApiReturnSite;
  is_editable: boolean;
  simplified_view?: boolean;
  dashboard_only_mode: boolean;
  grid_type: 'grids' | 'reports';
  grid_id: number;
  className?: string | null;
  draggableHandle: string;
}

export interface GridDataLoaderProps {
  project?: ApiReturnProject;
  site?: ApiReturnSite;
  is_editable: boolean;
  simplified_view?: boolean;
  dashboard_only_mode: boolean;
  grid_type: 'grids' | 'reports';
  grid_id: number;
  className?: string | null;
  draggableHandle: string;
  widgetCreationData: WidgetCreationData;
}

type Breakpoint = 'lg' | 'md' | 'sm' | 'xs' | 'xxs';

// -- constants --

const ResponseGridLayout = WidthProvider(EnhancedResponsiveGridLayout);

// -- helper functions --

const isLargeLayout = (currentBreakpoint: Breakpoint): boolean => {
  return currentBreakpoint === 'lg';
};

const addDashboardElementId =
  (mapping: WidgetIdToDashboardElementId) =>
  ({ i, x, y, h, w }: ReactGridLayout.Layout) => ({
    id: mapping[i]!,
    x,
    y,
    h,
    w,
  });

const gridLayoutToApiDashboardLayout =
  (mapping: WidgetIdToDashboardElementId) =>
  (layouts: ReactGridLayout.Layout[]): ApiDashboardLayoutParametersType => ({
    layouts: layouts
      .map(addDashboardElementId(mapping))
      .filter(({ id }) => id !== undefined),
  });

// Define grid properties
const getGridProps = (
  // TODO: remove nullability when report dashboards are stored
  mapping: WidgetIdToDashboardElementId | null,
  dashboard_id: number,
  type: 'grids' | 'reports',
  dashboardOnlyMode: boolean,
  dispatch: DashboardLibraryDispatch,
  dashboardApi: DashboardDataApi,
  cols?: { lg: number; md: number; sm: number; xs: number; xxs: number },
  rowHeight?: number,
  margin?: [number, number]
): EnhancedResponsiveGridLayoutProps => ({
  className: 'grid-layout',

  cols: cols ?? { lg: 24, md: 18, sm: 12, xs: 9, xxs: 6 },
  rowHeight: rowHeight ?? 16,

  isResizable: !dashboardOnlyMode,
  isDraggable: !dashboardOnlyMode,

  margin: margin ?? [6, 6],
  style: { marginBottom: '0' },
  containerPadding: [0, 0],

  draggableCancel: 'input, textarea, select, a, button, .plot',
  draggableHandle: '.react-grid-dragHandleExample',

  compactType: 'vertical',
  preventCollision: false,

  onWidthChange: () => {
    // FIXME: might be safer to use the useEffect hook with the grid's dimensions as the dependency. setTimeout does not guarantee that the grid has rerendered.
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 500);
  },

  onLayoutChange: (data, _, currentBreakpoint) => {
    // do not update the layout if the breakpoint is not 'lg'
    // until we can store different layouts for different breakpoints
    // in the database
    if (!isLargeLayout(currentBreakpoint)) return;
    // TODO: remove condition when report dashboard are stored in the database
    if (mapping) {
      const formattedData = gridLayoutToApiDashboardLayout(mapping)(data);
      // FIXME: should not be called if nothing was changed
      void dashboardApi.putDashboardLayout(dashboard_id, formattedData);
    }
    // wait until the grid is stable before dispatching the resize event
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    });
    dispatch(
      setDashboardLayout({
        type,
        dashboardId: dashboard_id,
        layoutName: 'lg',
        layoutValues: data,
      })
    );
  },
});

// -- components --

export function NoDashboard({
  type,
}: {
  type: 'grids' | 'reports';
}): JSX.Element {
  const dispatch = useDashboardLibraryDispatch();
  const { t } = useTranslation('dashboards');
  const permissionItem =
    type === 'grids' ? 'dashboard_create_button' : 'reports_create_button';
  return (
    <PermissionDecorator item={permissionItem} className="no-dashboard-permission">
      <div className="no-dashboard-empty">
        <div
          className="no-dashboard-card"
          role="button"
          tabIndex={0}
          onClick={() =>
            dispatch(
              setDashboardForm(
                type === 'grids'
                  ? 'UserDashboardFormOpen'
                  : 'ReportDashboardFormOpen'
              )
            )
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              dispatch(
                setDashboardForm(
                  type === 'grids'
                    ? 'UserDashboardFormOpen'
                    : 'ReportDashboardFormOpen'
                )
              );
            }
          }}
        >
          <div className="card-content">
            <Typography variant="h6" component="h5">
              {t('Configurer le premier')}{' '}
              {type === 'grids' ? t('tableau de bord') : t('rapport')}{' '}
              {t('pour ce site')}
            </Typography>
          </div>
          <ArrowForwardIcon className="arrow-icon" fontSize="small" />
        </div>
      </div>
    </PermissionDecorator>
  );
}

interface CreateWidgetElementProps<T extends WidgetNameType> {
  widget_id: string;
  dashboardElementId: number;
  site?: ApiReturnSite;
  is_editable: boolean;
  props: WidgetParamsFromName<T>;
  campaigns: ApiReturnCampaign[];
}

function createWidgetElement<T extends WidgetNameType>(
  type: T,
  params: CreateWidgetElementProps<T> & { dashboardOnlyMode: boolean }
) {
  return createElement(
    widget_configurations[type].component as unknown as FunctionComponent<
      WidgetParamsFromName<T>
    >,
    {
      key: params.widget_id,
      widget_id: params.widget_id,
      dashboardElementId: params.dashboardElementId,
      site: params.site,
      is_editable: params.is_editable,
      dashboardOnlyMode: params.dashboardOnlyMode,
      campaigns: params.campaigns,
      ...params.props,
    }
  );
}

type GridHelperComponentProps = GridComponentProps & {
  site?: ApiReturnSite;
  campaigns: ApiReturnCampaign[];
  widgetCreationData: WidgetCreationData;
  hydrationStatus: GridHydrationExternalsStatus;
  hydrationErrorMessage?: string;
  dashboardRepresentation?: ApiReturnDashboard;
};

/**
 * Render the dashboard and its widgets.
 */
const GridRendererComponent = (
  props: GridHelperComponentProps
): JSX.Element => {
  console.debug('[GridRendererComponent]');
  const dispatch = useDashboardLibraryDispatch();
  const host = useDashboardHost();
  const hydrationStartedForGridId = useRef<number | null>(null);
  const [gridPrepareError, setGridPrepareError] = useState<
    string | undefined
  >();
  const dashboardSlice = useDashboardLibrarySelector(
    (state) => state.dashboard
  );
  const widgetConfigPanelState = useDashboardLibrarySelector(
    (state) => state.dashboard.ConfigWidgetPanelState
  );

  const gridId = props.grid_id;
  const grid = useDashboardLibrarySelector(
    (state) => state.dashboard[props.grid_type][gridId]
  );
  const gridLayoutSettings = grid?.additionalProperties;
  const isSvgConstrained = gridLayoutSettings?.layoutType === 'svg-constrained';
  const freeLayoutSettings =
    gridLayoutSettings?.layoutType === 'free' ? gridLayoutSettings : undefined;
  const canAddWidget = isSvgConstrained
    ? hasAvailableWidgetSlots(
        gridLayoutSettings.widgetSlot,
        Object.keys(grid?.widgets ?? {}).length
      )
    : true;
  const widgetFormIsOpen = useDashboardLibrarySelector(
    (state) => state.dashboard.widgetFormIsOpen
  );
  // for imageWidget config.
  const config = useDashboardLibrarySelector(
    (state) => state.dashboard.resourceSelectorConfig
  );
  // for imageGallery config.
  const imageGalleryConfig = useDashboardLibrarySelector(
    (state) => state.dashboard.imageGallerySelectorConfig
  );
  const deleteWidget = useDashboardLibrarySelector(
    (state) => state.dashboard.deleteConfirmationModalIsOpen
  );

  const widgetKeyToDashboardElementId = useDashboardLibrarySelector(
    (state) =>
      getDashboardElementIdMappingFromDashboard(gridId, props.grid_type, state)!
  );
  // filter dashboardelements
  const dashboardDropdownItems = buildWidgetDropdownItems(
    props.widgetCreationData.widgets,
    props.widgetCreationData.mfes
  );
  const { t } = useTranslation('dashboards');

  const dashboardElements: DropDownMenuItem[] = dashboardDropdownItems.map(
    (item) => ({
      value: item.value,
      label: item.label,
      category:
        item.kind === 'plugin-group' ? t('Plugins') : t('Widgets intégrés'),
    })
  );

  useEffect(() => {
    hydrationStartedForGridId.current = null;
    setGridPrepareError(undefined);
  }, [gridId]);

  useEffect(() => {
    if (
      props.hydrationStatus !== 'ready' ||
      grid !== undefined ||
      gridId === 0 ||
      props.dashboardRepresentation === undefined
    ) {
      return;
    }

    if (hydrationStartedForGridId.current === gridId) {
      return;
    }
    hydrationStartedForGridId.current = gridId;

    const startDate = new Date();
    const endDate = new Date();
    const dashboardData = props.dashboardRepresentation;

    void (async () => {
      try {
        const sourceLabelMap = await fetchSourceLabelMapForElements(
          host.api.sources.getLabels,
          dashboardData.elements,
          {
            project_id: host.project_id ?? undefined,
            site_id: host.site_id ?? undefined,
          }
        );

        dispatch((_, getState) => {
          try {
            if (props.grid_type === 'grids') {
              const dep = {
                sourceLabelMap,
                startDate,
                endDate,
                w: 12,
                h: 9,
              };
              const newStateWithGridAdded = addDashboardSafely(
                dep,
                'grids',
                dashboardData,
                getState()
              );
              dispatch(hydrateDashboardLibraryState(newStateWithGridAdded));
              return;
            }

            const dep = {
              sourceLabelMap,
              startDate,
              endDate,
              w: 12,
              h: 9,
            };
            const mapping = R.swapKeysAndValues(
              buildMapping(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (dashboardData as ReportDashboard)
                  .report_config! as unknown as ReportStructure
              )
            );
            const finalDep = {
              ...dep,
              elementNameToVariableName: mapping,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              reportStructure: (dashboardData as ReportDashboard)
                .report_config! as unknown as ReportStructure,
              layouts: [],
            };
            const newStateWithReportAdded = addDashboardSafely(
              finalDep,
              'reports',
              dashboardData,
              getState()
            );
            dispatch(hydrateDashboardLibraryState(newStateWithReportAdded));
          } catch (error: unknown) {
            hydrationStartedForGridId.current = null;
            setGridPrepareError(
              error instanceof Error
                ? error.message
                : 'Impossible de préparer le tableau de bord'
            );
          }
        });
      } catch (error: unknown) {
        hydrationStartedForGridId.current = null;
        setGridPrepareError(
          error instanceof Error
            ? error.message
            : 'Impossible de préparer le tableau de bord'
        );
      }
    })();
  }, [
    dispatch,
    grid,
    gridId,
    host.api.sources.getLabels,
    host.project_id,
    host.site_id,
    props.dashboardRepresentation,
    props.grid_type,
    props.hydrationStatus,
  ]);

  const useFullSpaceMode =
    props.simplified_view && Object.entries(grid?.widgets ?? {}).length === 1;

  const renderResizeHandle = (
    axis: string,
    ref: React.Ref<HTMLElement>
  ): JSX.Element => (
    <PermissionDecorator
      ref={ref as React.Ref<HTMLDivElement>}
      item={'dashboard_resize_widget'}
      project_id={props.project?.id}
      site_id={props.site?.id}
      extra={{ dashboard_id: props.grid_id }}
      className={`react-resizable-handle react-resizable-handle-${axis}`}
    />
  );

  const showNoDashboard =
    !grid &&
    !gridPrepareError &&
    props.hydrationStatus !== 'error' &&
    !(
      gridId !== 0 &&
      (props.hydrationStatus === 'loading' ||
        (props.hydrationStatus === 'ready' && grid === undefined))
    );

  return (
    <>
      <GenericErrorBoundary>
        {props.dashboard_only_mode && grid && (
          <Typography
            variant="h4"
            align="center"
            color="primary"
            sx={{ mb: 2 }}
          >
            {grid?.name}
          </Typography>
        )}
        {!props.dashboard_only_mode && (
          <div>
            <div style={{ display: 'flex' }}>
              <DashboardTabBarComponent
                gridType={props.grid_type}
                {...(props.project && props.site
                  ? {
                      mode: 'forsite',
                      projectId: props.project.id,
                      siteId: props.site.id,
                    }
                  : {
                      mode: 'contextfree',
                      // callback: (dashboardId) => setGridId(dashboardId),
                    })}
                gridId={gridId}
                simplifiedView={props.simplified_view}
                itemNameCreateBtn={props.itemNameCreateBtn}
                itemNameDeleteBtn={props.itemNameDeleteBtn}
                itemNameEditEl={props.itemNameEditEl}
              />
            </div>
            {grid?.name && props.itemNameAddWidget && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  marginRight: '16px',
                  padding: '0px 16px 0px 16px',
                  position: 'sticky',
                  top: '8px',
                  zIndex: '20',
                  backgroundColor: '#f8f8f8',
                  borderRadius: '8px',
                }}
              >
                {props.grid_type === 'grids' && props.project && props.site ? (
                  <PublicDashboardButtonComponent
                    id={gridId}
                    projectId={props.project.id}
                    siteId={props.site.id}
                    publicUrl={grid.publicUrl}
                  />
                ) : (
                  <div />
                )}
                {/* Button that opens menu of widgets */}
                <PermissionDecorator
                  item={props.itemNameAddWidget}
                  project_id={props.project?.id}
                  site_id={props.site?.id}
                  style={
                    useFullSpaceMode
                      ? {
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          zIndex: '20',
                        }
                      : {}
                  }
                >
                  <DropDownMenu
                    label={`+ ${t('Ajouter')}`}
                    items={dashboardElements}
                    disabled={!canAddWidget}
                    onClick={(value) => {
                      const item = dashboardDropdownItems.find(
                        (entry) => entry.value === value
                      );
                      if (!item) {
                        return;
                      }

                      if (item.kind === 'standalone') {
                        dispatch(
                          setWidgetFormIsOpen({
                            state: 'open',
                            elementType: item.elementType!,
                            formTitle: item.formTitle,
                          })
                        );
                        return;
                      }

                      if (item.kind === 'builtin-group') {
                        const groupConfig = getBuiltinGroupConfig(
                          item.groupId!
                        );
                        if (!groupConfig) {
                          return;
                        }
                        dispatch(
                          setWidgetFormIsOpen({
                            state: 'open',
                            widgetGroupId: item.groupId,
                            elementType: groupConfig.members[0],
                            formTitle: item.formTitle,
                          })
                        );
                        return;
                      }

                      dispatch(
                        setWidgetFormIsOpen({
                          state: 'open',
                          widgetGroupId: item.groupId,
                          elementType: 'remotewidget',
                          formTitle: item.formTitle,
                        })
                      );
                    }}
                  />
                </PermissionDecorator>
              </div>
            )}
          </div>
        )}
        <div
          className={`sites-grid sites-grid-dashboard ${
            isSvgConstrained ? 'sites-grid--svg-constrained' : ''
          } ${showNoDashboard ? 'sites-grid--empty' : ''} ${
            props.className || ''
          }`}
        >
          {grid ? (
            useFullSpaceMode ? (
              [...Object.entries(grid.widgets)].map(
                ([
                  widget_id,
                  { type, props: widgetProps, dashboardElementId },
                ]) => (
                  <div
                    key={widget_id}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    {createWidgetElement(type, {
                      widget_id,
                      dashboardElementId,
                      site: props.site,
                      is_editable: props.is_editable,
                      dashboardOnlyMode: props.dashboard_only_mode,
                      campaigns: props.campaigns,
                      props: {
                        gridId: gridId,
                        ...widgetProps,
                      },
                    })}
                  </div>
                )
              )
            ) : isSvgConstrained ? (
              <SvgConstrainedGridLayout
                svgViewBox={gridLayoutSettings.svgViewBox}
                svgData={gridLayoutSettings.svgData}
                layout={grid.layouts.lg ?? []}
              >
                {[...Object.entries(grid.widgets)].map(
                  ([
                    widget_id,
                    { type, props: widgetProps, dashboardElementId },
                  ]) => (
                    <div key={widget_id}>
                      {createWidgetElement(type, {
                        widget_id,
                        dashboardElementId,
                        site: props.site,
                        is_editable:
                          props.is_editable && !props.dashboard_only_mode,
                        dashboardOnlyMode: props.dashboard_only_mode,
                        campaigns: props.campaigns,
                        props: {
                          gridId: gridId,
                          ...widgetProps,
                        },
                      })}
                    </div>
                  )
                )}
              </SvgConstrainedGridLayout>
            ) : (
              // Default: display widgets in a grid with knobs to move and resize
              <ResponseGridLayout
                layouts={grid.layouts}
                resizeHandle={renderResizeHandle}
                {...getGridProps(
                  widgetKeyToDashboardElementId,
                  gridId,
                  props.grid_type,
                  props.dashboard_only_mode,
                  dispatch,
                  host.api.dashboard,
                  freeLayoutSettings?.cols,
                  freeLayoutSettings?.rowHeight,
                  freeLayoutSettings?.margin
                )}
              >
                {[...Object.entries(grid.widgets)].map(
                  ([
                    widget_id,
                    { type, props: widgetProps, dashboardElementId },
                  ]) => (
                    <div key={widget_id}>
                      {createWidgetElement(type, {
                        widget_id,
                        dashboardElementId,
                        site: props.site,
                        is_editable:
                          props.is_editable && !props.dashboard_only_mode,
                        dashboardOnlyMode: props.dashboard_only_mode,
                        campaigns: props.campaigns,
                        props: {
                          gridId: gridId,
                          ...widgetProps,
                        },
                      })}
                    </div>
                  )
                )}
              </ResponseGridLayout>
            )
          ) : gridPrepareError ? (
            <StateComponent
              name="Impossible de charger le tableau de bord"
              message={gridPrepareError}
            />
          ) : props.hydrationStatus === 'error' ? (
            <StateComponent
              name="Impossible de charger le tableau de bord"
              message={props.hydrationErrorMessage ?? ''}
            />
          ) : gridId !== 0 &&
            (props.hydrationStatus === 'loading' ||
              (props.hydrationStatus === 'ready' && grid === undefined)) ? (
            <Typography align="center" sx={{ my: 2 }}>
              Chargement du tableau de bord...
            </Typography>
          ) : (
            <NoDashboard type={props.grid_type} />
          )}
        </div>
        <RemoteConfigSidePanel
          site={props.site}
          project_id={props.project?.id}
        />
      </GenericErrorBoundary>
      <InitiWidgetFormHandler
        title={
          widgetFormIsOpen.state === 'open' && widgetFormIsOpen.formTitle
            ? widgetFormIsOpen.formTitle
            : 'Ajouter un élément'
        }
        isOpen={widgetFormIsOpen.state === 'open'}
        withOpenButton={false}
        gridType={props.grid_type}
        onCancel={() => dispatch(setWidgetFormIsOpen({ state: 'closed' }))}
        onSave={() => dispatch(setWidgetFormIsOpen({ state: 'closed' }))}
        grid_id={gridId}
      />
      {(widgetConfigPanelState.state === 'Configuring' ||
        widgetConfigPanelState.state === 'Reconfiguring') &&
        getWidgetFromIdMapping(
          widgetConfigPanelState.widgetId.toString(),
          widgetConfigPanelState.gridType,
          widgetConfigPanelState.gridId,
          dashboardSlice
        ) &&
        Number(gridId) === widgetConfigPanelState.gridId && (
          <ConfigWidgetFormHandler
            title={''}
            isOpen={
              widgetConfigPanelState.state === 'Configuring' ||
              widgetConfigPanelState.state === 'Reconfiguring'
            }
            mode={
              widgetConfigPanelState.state === 'Reconfiguring'
                ? 'reconfiguring'
                : 'configuring'
            }
            withOpenButton={false}
            gridType={widgetConfigPanelState.gridType}
            onCancel={() =>
              dispatch(setWidgetConfigPanelState({ state: 'NotConfiguring' }))
            }
            widgetId={widgetConfigPanelState.widgetId}
            grid_id={widgetConfigPanelState.gridId}
          />
        )}

      {config.state === 'open' && (
        <ImageWidgetConfig
          project_id={config.projectId!}
          site_id={config.siteId!}
          open={true}
          grid_type={config.grid_type}
        />
      )}

      {imageGalleryConfig.state === 'open' && (
        <ImageGalleryConfig
          project_id={imageGalleryConfig.projectId!}
          site_id={imageGalleryConfig.siteId!}
          open={true}
          grid_type={imageGalleryConfig.grid_type}
          grid_id={imageGalleryConfig.dashboardId}
          widget_id={imageGalleryConfig.widgetId}
        />
      )}

      {deleteWidget.state === 'open' && (
        <DeleteWidgetConfirmation
          dashboardElementId={deleteWidget.dashboardElementId}
          gridType={props.grid_type}
          widgetName={deleteWidget.widgetName}
        />
      )}
    </>
  );
};

/**
 * Prefetches hydration externals in the background and renders the grid shell
 * immediately (widgets and forms keep their own loading boundaries).
 */
function GridDataLoader(props: GridDataLoaderProps) {
  const host = useDashboardHost();
  const [dashboardLoad, setDashboardLoad] = useState<
    | { readonly status: 'idle' }
    | { readonly status: 'loading'; readonly gridId: number }
    | { readonly status: 'error'; readonly message: string }
    | {
        readonly status: 'ready';
        readonly data: ApiReturnDashboard;
        readonly gridId: number;
      }
  >({ status: 'idle' });

  useEffect(() => {
    const requestedGridId = props.grid_id;
    if (requestedGridId === 0) {
      return;
    }

    let cancelled = false;
    setDashboardLoad({ status: 'loading', gridId: requestedGridId });
    host.api.dashboard
      .getDashboard(requestedGridId)
      .then((data) => {
        if (!cancelled) {
          setDashboardLoad({ status: 'ready', data, gridId: requestedGridId });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDashboardLoad({
            status: 'error',
            message: 'Impossible de charger le tableau de bord',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [host.api.dashboard, props.grid_id]);

  const dashboardReadyForGrid =
    dashboardLoad.status === 'ready' && dashboardLoad.gridId === props.grid_id;

  const hydration = useGridHydrationExternals({
    siteId: props.site?.id,
    projectRef: props.project,
    siteRef: props.site,
  });

  const combinedHydrationStatus: GridHydrationExternalsStatus =
    dashboardLoad.status === 'idle' ||
    dashboardLoad.status === 'loading' ||
    (dashboardLoad.status === 'ready' && !dashboardReadyForGrid)
      ? 'loading'
      : dashboardLoad.status === 'error'
      ? 'error'
      : hydration.status;

  const combinedHydrationErrorMessage =
    dashboardLoad.status === 'error'
      ? dashboardLoad.message
      : hydration.status === 'error'
      ? hydration.message
      : undefined;

  const snapshot: GridHydrationSnapshot =
    hydration.status === 'ready'
      ? hydration.snapshot
      : emptyGridHydrationSnapshot();

  const dashboardRepresentation = dashboardReadyForGrid
    ? dashboardLoad.data
    : undefined;

  return (
    <GridRendererComponent
      {...props}
      project={snapshot.project ?? props.project}
      site={snapshot.site ?? props.site}
      campaigns={snapshot.campaigns}
      hydrationStatus={combinedHydrationStatus}
      hydrationErrorMessage={combinedHydrationErrorMessage}
      dashboardRepresentation={dashboardRepresentation}
    />
  );
}

// -- main components --

/**
 * Loads the data of a dashboard before displaying it with its widgets.
 */
function GridComponent(
  props: GridComponentProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const widgetCreationInfos = useWidgetCreationInfo();

  if (widgetCreationInfos.state === 'pending') {
    return <div>chargement des éléments...</div>;
  }

  return (
    <GridDataLoader {...props} widgetCreationData={widgetCreationInfos.data} />
  );
}

export default forwardRef(GridComponent);
